import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { uploadImage, resolveImageUrl } from '../../lib/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: '', description: '', images: [], amenities: '', urlInput: '' };

const StudiosManagement = () => {
  const [studios, setStudios] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStudio, setEditingStudio] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchStudios();
  }, []);

  const fetchStudios = async () => {
    try {
      const response = await axios.get(`${API}/studios`);
      setStudios(response.data);
    } catch (error) {
      console.error('Error fetching studios:', error);
      toast.error('Failed to load studios');
    }
  };

  const handleImage = async (file) => {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setForm((p) => ({ ...p, images: [...p.images, url] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload image');
    }
  };

  const addImageUrl = () => {
    const url = (form.urlInput || '').trim();
    if (!url) return;
    setForm((p) => ({ ...p, images: [...p.images, url], urlInput: '' }));
  };

  const removeImage = (idx) => {
    setForm((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));
  };

  const moveImage = (idx, dir) => {
    setForm((p) => {
      const arr = [...p.images];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return p;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...p, images: arr };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      description: form.description,
      images: form.images,
      image_url: form.images[0] || '',
      amenities: form.amenities.split(',').map((a) => a.trim()).filter(Boolean),
    };
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingStudio) {
        await axios.put(`${API}/studios/${editingStudio.id}`, payload, { headers });
        toast.success('Studio updated successfully');
      } else {
        await axios.post(`${API}/studios`, payload, { headers });
        toast.success('Studio created successfully');
      }
      fetchStudios();
      handleClose();
    } catch (error) {
      console.error('Error saving studio:', error);
      toast.error('Failed to save studio');
    }
  };

  const handleDelete = async (studioId) => {
    if (!window.confirm('Delete this studio? Existing bookings are not removed.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/studios/${studioId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Studio deleted');
      fetchStudios();
    } catch (error) {
      console.error('Error deleting studio:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete studio');
    }
  };

  const handleEdit = (studio) => {
    setEditingStudio(studio);
    const imgs = (studio.images && studio.images.length > 0)
      ? studio.images
      : (studio.image_url ? [studio.image_url] : []);
    setForm({
      name: studio.name,
      description: studio.description,
      images: imgs,
      amenities: (studio.amenities || []).join(', '),
      urlInput: '',
    });
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingStudio(null);
    setForm(emptyForm);
  };

  return (
    <div className="p-8" data-testid="studios-management">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
          Studios Management
        </h2>
        <button
          onClick={() => setIsDialogOpen(true)}
          className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2"
          data-testid="add-studio-btn"
        >
          <Plus size={20} />
          ADD STUDIO
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {studios.map((studio) => (
          <div key={studio.id} className="bg-white border border-zinc-200 overflow-hidden" data-testid={`studio-admin-card-${studio.id}`}>
            <div className="h-40 bg-cover bg-center bg-zinc-100" style={{ backgroundImage: `url('${resolveImageUrl(studio.image_url)}')` }} />
            <div className="p-4">
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>{studio.name}</h3>
              <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{studio.description}</p>
              <div className="flex flex-wrap gap-1 mb-4">
                {(studio.amenities || []).map((a, i) => (
                  <span key={i} className="text-xs bg-zinc-100 px-2 py-1 rounded">{a}</span>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(studio)} className="flex-1 bg-zinc-100 hover:bg-zinc-200 text-black rounded px-4 py-2 text-sm font-medium flex items-center justify-center gap-2" data-testid={`edit-studio-${studio.id}`}>
                  <Edit size={16} /> EDIT
                </button>
                <button onClick={() => handleDelete(studio.id)} className="flex-1 bg-red-100 hover:bg-red-200 text-red-600 rounded px-4 py-2 text-sm font-medium flex items-center justify-center gap-2" data-testid={`delete-studio-${studio.id}`}>
                  <Trash2 size={16} /> DELETE
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="studio-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              {editingStudio ? 'Edit Studio' : 'Add New Studio'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="studio-name">Studio Name *</Label>
              <Input id="studio-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="studio-name-input" />
            </div>
            <div>
              <Label htmlFor="studio-desc">Description *</Label>
              <Textarea id="studio-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="studio-description-input" />
            </div>
            <div>
              <Label htmlFor="studio-amenities">Amenities (comma-separated)</Label>
              <Input id="studio-amenities" value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} placeholder="Mirrors, Sound System, AC" data-testid="studio-amenities-input" />
            </div>
            <div>
              <Label>Photo Gallery</Label>
              <p className="text-xs text-zinc-500 mb-2">Add multiple photos. The first photo is the cover. Use arrows to reorder.</p>
              <div className="flex gap-2">
                <Input
                  value={form.urlInput}
                  onChange={(e) => setForm({ ...form, urlInput: e.target.value })}
                  placeholder="Paste image URL..."
                  data-testid="studio-image-url-input"
                />
                <button type="button" onClick={addImageUrl} className="bg-zinc-900 text-white rounded px-4 text-sm font-medium" data-testid="add-image-url-btn">Add URL</button>
              </div>
              <label htmlFor="studio-image-file" className="mt-2 flex items-center gap-2 cursor-pointer bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 px-4 py-3 w-fit transition-colors" data-testid="studio-image-upload-label">
                <Upload size={18} /> Upload a photo
                <input id="studio-image-file" type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} data-testid="studio-image-upload-input" />
              </label>

              {form.images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3" data-testid="studio-gallery">
                  {form.images.map((img, idx) => (
                    <div key={idx} className="relative border border-zinc-200" data-testid={`gallery-item-${idx}`}>
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 bg-[var(--accent)] text-black text-[10px] font-bold px-1.5 py-0.5 rounded z-10">COVER</span>
                      )}
                      <img src={resolveImageUrl(img)} alt={`Studio ${idx + 1}`} className="h-24 w-full object-cover" />
                      <div className="flex justify-between items-center bg-zinc-50 px-1 py-1">
                        <button type="button" onClick={() => moveImage(idx, -1)} disabled={idx === 0} className="text-xs px-1 disabled:opacity-30" data-testid={`move-left-${idx}`}>◀</button>
                        <button type="button" onClick={() => removeImage(idx)} className="text-red-600 text-xs px-1" data-testid={`remove-image-${idx}`}>Delete</button>
                        <button type="button" onClick={() => moveImage(idx, 1)} disabled={idx === form.images.length - 1} className="text-xs px-1 disabled:opacity-30" data-testid={`move-right-${idx}`}>▶</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95" data-testid="save-studio-btn">
                {editingStudio ? 'UPDATE' : 'CREATE'}
              </button>
              <button type="button" onClick={handleClose} className="flex-1 border-2 border-black text-black hover:bg-black hover:text-white rounded-full px-6 py-3 transition-colors" data-testid="cancel-studio-btn">
                CANCEL
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudiosManagement;
